import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, Ban } from 'lucide-react';
import { TableSkeleton } from '@components/medicus-ui/Loaders';
import { getBloqueados } from '@lib/services/bloqueados.service';

const PAGE_SIZE = 100;

/**
 * Bloqueados: universo de personas con el flag `bloqueado` (frenadas a nivel
 * persona). SOLO LECTURA — el bloqueo/desbloqueo se hace por script. Gateada por
 * `view:bloqueados` (default solo SUPERADMIN).
 */
export function BloqueadosPage() {
  const [q, setQ] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [page, setPage] = useState(1);

  const query = useQuery({
    queryKey: ['bloqueados', busqueda, page],
    queryFn: () => getBloqueados(page, PAGE_SIZE, busqueda),
  });

  const total = query.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  function buscar(e: React.FormEvent) {
    e.preventDefault();
    setBusqueda(q.trim());
    setPage(1);
  }

  return (
    <section>
      <header className="pageHeader">
        <h1 className="heading-3" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Ban size={22} style={{ color: '#dc2626' }} /> Bloqueados
        </h1>
        <p className="text-element-mid">
          Personas bloqueadas a nivel individuo: no aparecen en el grupo del onboarding (si son
          familiares), frenan el onboarding (si son titulares) y no se adjuntan al alta. Solo lectura.
        </p>
      </header>

      <form onSubmit={buscar} style={{ display: 'flex', gap: 8, margin: '18px 0', maxWidth: 460 }}>
        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '0 12px',
            border: '1px solid var(--color-outline)',
            borderRadius: 'var(--radius-md)',
            background: 'var(--color-background)',
          }}
        >
          <Search size={16} style={{ opacity: 0.6 }} />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="DNI, legajo o nombre"
            style={{ flex: 1, border: 'none', outline: 'none', padding: '10px 0', background: 'transparent', fontSize: 14 }}
          />
        </div>
        <button type="submit" className="btn btn-primary">Buscar</button>
      </form>

      {query.isLoading && <TableSkeleton rows={6} />}
      {query.isError && <p className="status status-error">No se pudieron cargar los bloqueados.</p>}
      {query.data && (
        <>
          <p className="text-element-mid" style={{ fontSize: 13, margin: '0 0 8px' }}>
            {total} {total === 1 ? 'persona bloqueada' : 'personas bloqueadas'}
            {busqueda && ` para "${busqueda}"`}
          </p>
          {query.data.items.length === 0 ? (
            <p className="text-element-mid">No hay personas bloqueadas que coincidan.</p>
          ) : (
            <div className="tableWrap">
              <table className="dataTable">
                <thead>
                  <tr>
                    <th>Persona</th>
                    <th>DNI</th>
                    <th>ID Web</th>
                    <th>Legajo</th>
                    <th>Rol</th>
                    <th>Motivo</th>
                    <th>Bloqueado</th>
                  </tr>
                </thead>
                <tbody>
                  {query.data.items.map((b) => (
                    <tr key={b.asociadoId}>
                      <td style={{ fontWeight: 600 }}>{b.nombre || '—'}</td>
                      <td>{b.documento || '—'}</td>
                      <td style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--color-element-mid)' }}>
                        {b.asociadoId}
                      </td>
                      <td>{b.legajo || '—'}</td>
                      <td>
                        <span style={{ fontWeight: 600, color: b.esTitular ? 'var(--color-primary-darker)' : 'var(--color-element-mid)' }}>
                          {b.esTitular ? 'Titular' : 'Familiar'}
                        </span>
                      </td>
                      <td style={{ color: 'var(--color-element-mid)' }}>{b.motivo || '—'}</td>
                      <td style={{ color: 'var(--color-element-mid)', whiteSpace: 'nowrap' }}>
                        {b.bloqueadoAt ? new Date(b.bloqueadoAt).toLocaleDateString('es-AR') : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {totalPages > 1 && (
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 14 }}>
              <button type="button" className="btn btn-ghost" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                Anterior
              </button>
              <span style={{ fontSize: 13, color: 'var(--color-element-mid)' }}>
                Página {page} de {totalPages}
              </span>
              <button type="button" className="btn btn-ghost" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                Siguiente
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
}
